import React, { useState, useEffect, useRef } from 'react';
import { Taskbar } from './Taskbar';
import { StartMenu } from './StartMenu';
import { DesktopIcon } from './DesktopIcon';
import { WindowManager } from './WindowManager';
import { Screensaver } from './Screensaver';
import { ContextMenu, useContextMenu, ContextMenuItem } from './ui/context-menu-custom';
import { useOS } from '../hooks/useOS';
import { useAuth } from '../hooks/useAuth';
import { useUserDesktopConfig, useUserSettings } from '../hooks/useUserSettings';
import { globalNavigator } from './WindowContent';
import { FolderPlus, FileText, Settings, Monitor, Paintbrush, Grid3X3, Folder } from 'lucide-react';
import { SplashScreen } from './SplashScreen';
import DesktopAssistant from './DesktopAssistant';
import AICreditAlertDialog from './AICreditAlertDialog';
import { ResetSuccessPopup } from './ResetSuccessPopup';
import { runBootScripts } from '../utils/bootScripts';
import SecurityLockOverlay from './SecurityLockOverlay';

import StartupSound from './StartupSound';
import { useSettings } from '../hooks/useSettings';
import { getEffectiveVolume } from '../lib/sound';
import { captureDesktopToPng, saveScreenshotToVFS } from '../utils/screenshot';
import { useToast } from './ui/use-toast';
import { getAssetPath } from '../utils/assetPath';
import { makeOverrideIcon, makeProgramIcon } from './AppIcon';
import { SystemMonitorWidget } from './SystemMonitorWidget';
import { verifyStoredPin } from '../lib/securityPin';
import { DEFAULT_SECURITY_SETTINGS, type SecuritySettingsState } from '../lib/securitySettings';

const NewMenuIcon = makeOverrideIcon('new-menu', FolderPlus);
const NewFolderMenuIcon = makeOverrideIcon('folder', Folder);
const NewTextDocumentMenuIcon = makeOverrideIcon('text-file', FileText);
const ArrangeDesktopIconsMenuIcon = makeOverrideIcon('arrange-desktop-icons', Grid3X3);
const DisplaySettingsMenuIcon = makeOverrideIcon('display-properties', Monitor);
const PersonaliseMenuIcon = makeOverrideIcon('personalise', Paintbrush);
const DesktopTextFileIcon = makeProgramIcon('text-file', 'document');
const MapleSeedDesktopFileIcon = makeProgramIcon('maple-seed', 'archive');
const GAME_APP_IDS = new Set(['flight-sim', 'grand-theft-boxy', 'cortex', 'DOSEmulator', 'universal-emulator']);
const RECYCLE_BIN_PATH = 'C:\\$Recycle.Bin';
const DESKTOP_ASSISTANT_STORAGE_KEY = 'mapleos.desktopAssistant';

interface DesktopProps {
  shellOverlayActive?: boolean;
}

export const Desktop: React.FC<DesktopProps> = ({ shellOverlayActive = false }) => {
  const { 
    openWindows, 
    openWindow, 
    closeWindow, 
    minimizeWindow, 
    maximizeWindow,
    updateWindowBounds,
    focusWindow,
    isStartMenuOpen,
    toggleStartMenu,
    desktopIcons,
    updateDesktopIconPosition,
    addDesktopIcon,
    removeDesktopIcon,
    updateDesktopIcon,
    lastActivity,
    updateActivity
  } = useOS();

  const { user, profile } = useAuth();
  const { desktopConfig, saveDesktopConfig, isLoaded: desktopConfigLoaded } = useUserDesktopConfig();
  
  // Load display settings from Supabase  
  const { settings: displaySettings, isLoaded: displayLoaded } = useUserSettings('display', {
    selectedBackground: 'default',
    selectedAccent: 'red',
    screensaverType: 'bouncing',
    screensaverDuration: 5, // minutes
    isDarkMode: true, // was false; default to dark on first load when no settings exist
    useAccentTitlebar: true,
    desktopColor: 'default',
    iconTextColor: 'default',
    customIconTextColor: '#ffffff',
    iconBackgroundOpacity: 0.45,
  });

  // Load sound settings from Supabase
  const { settings: soundSettings, isLoaded: soundLoaded } = useSettings('sound', {
    masterVolume: 75,
    systemVolume: 60,
    micVolume: 50,
    isMuted: false,
    micMuted: false,
    outputDevice: 'speakers',
    inputDevice: 'microphone',
    soundScheme: 'default',
    startupEnabled: true,
    spatialAudio: false,
    noiseCancellation: false,
    audioEnhancements: false,
    startupSoundUrl: '/os/assets/audio/startup.mp3',
  });
  const { settings: rawSecuritySettings, isLoaded: securityLoaded } = useUserSettings('security', DEFAULT_SECURITY_SETTINGS);
  const securitySettings = rawSecuritySettings as SecuritySettingsState;
  const { settings: osAISettings } = useUserSettings('mapleos-ai', {
    defaultModelId: 'maplept-free',
    hideDesktopTaskbar: false,
  });

  const [screensaverActive, setScreensaverActive] = useState(false);
  const [currentBackground, setCurrentBackground] = useState<string>('');
  const [selectedIconId, setSelectedIconId] = useState<string | null>(null);
  const [showSplash, setShowSplash] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [shouldPlayStartupSound, setShouldPlayStartupSound] = useState(false);
  const [isDragOverDesktop, setIsDragOverDesktop] = useState(false);
  const [lockOverlayMode, setLockOverlayMode] = useState<null | 'screen' | 'fullscreen-exit'>(null);
  const [lockError, setLockError] = useState('');
  const [electronFullScreen, setElectronFullScreen] = useState(false);
  const [recycleBinHasItems, setRecycleBinHasItems] = useState(false);
  const [aiTaskbarPinned, setAiTaskbarPinned] = useState(() => {
    try {
      const raw = localStorage.getItem(DESKTOP_ASSISTANT_STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { pinned?: boolean };
      return Boolean(parsed.pinned);
    } catch {
      return false;
    }
  });
  const [hideAiTaskbar, setHideAiTaskbar] = useState(Boolean(osAISettings.hideDesktopTaskbar));

  const visibleOpenWindows = openWindows.filter((window) => !window.hiddenInTray);
  const messengerTrayWindow = openWindows.find((window) => window.appId === 'messenger') ?? null;

  const activeWindow = visibleOpenWindows.reduce<(typeof visibleOpenWindows)[number] | null>((topWindow, candidate) => {

    if (candidate.isMinimized) {
      return topWindow;
    }

    if (!topWindow || candidate.zIndex > topWindow.zIndex) {
      return candidate;
    }

    return topWindow;
  }, null);
  const gameWindowActive = activeWindow ? GAME_APP_IDS.has(activeWindow.appId) : false;
  const isElectron = typeof window !== 'undefined' && Boolean(window.electronAPI?.isElectron);
  const desktopTopInset = isElectron && !electronFullScreen ? 32 : 0;
  const pinConfigured = Boolean(securitySettings.pinHash && securitySettings.pinSalt);
  const fullscreenExitBypassRef = useRef(false);
  const previousElectronFullScreenRef = useRef(false);

  const handleMessengerTrayClick = () => {
    if (!messengerTrayWindow) {
      return;
    }

    if (messengerTrayWindow.hiddenInTray) {
      focusWindow(messengerTrayWindow.id);
      return;
    }

    closeWindow(messengerTrayWindow.id);
  };

  // ADD: initialize toast function from shadcn use-toast hook
  const { toast } = useToast();

  // NEW: ref to the desktop container for drop coordinate calculations
  const desktopRef = useRef<HTMLDivElement>(null);

  // NEW: persistent map of filePath -> {x,y} positions
  const ICON_POS_KEY = 'MapleOS_icon_positions_v1';
  const [iconPositions, setIconPositions] = useState<Record<string, { x: number; y: number }>>(() => {
    try {
      const raw = localStorage.getItem(ICON_POS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(ICON_POS_KEY, JSON.stringify(iconPositions));
    } catch {
      // ignore storage errors
    }
  }, [iconPositions]);

  useEffect(() => {
    setHideAiTaskbar(Boolean(osAISettings.hideDesktopTaskbar));
  }, [osAISettings.hideDesktopTaskbar]);

  useEffect(() => {
    const handleAISettingsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ hideDesktopTaskbar?: boolean }>).detail;
      if (typeof detail?.hideDesktopTaskbar === 'boolean') {
        setHideAiTaskbar(detail.hideDesktopTaskbar);
      }
    };

    window.addEventListener('mapleos-ai-settings-changed', handleAISettingsChanged as EventListener);
    return () => window.removeEventListener('mapleos-ai-settings-changed', handleAISettingsChanged as EventListener);
  }, []);

  // Keep a stable ref of current desktop icons for event handlers
  const iconsRef = useRef(desktopIcons);
  useEffect(() => {
    iconsRef.current = desktopIcons;
  }, [desktopIcons]);

  // NEW: emit mapleos:ready after wallpaper is applied and loaded (single-fire guard)
  const readyEmittedRef = useRef(false);
  const emitReady = (reason: string) => {
    if (readyEmittedRef.current) return;
    readyEmittedRef.current = true;
    console.log('[Desktop] mapleos:ready dispatch', { reason, currentBackground });
    window.dispatchEvent(new CustomEvent('mapleos:ready'));
  };

  useEffect(() => {
    // If there is a wallpaper URL, wait for image load before emitting
    if (currentBackground) {
      const img = new Image();
      img.onload = () => emitReady('wallpaper-loaded');
      img.onerror = () => emitReady('wallpaper-error');
      img.src = currentBackground;
      return;
    }
    // No wallpaper set: emit as soon as Desktop mounts visuals
    const t = setTimeout(() => emitReady('no-wallpaper'), 0);
    return () => clearTimeout(t);
  }, [currentBackground]);

  const openLockOverlay = (mode: 'screen' | 'fullscreen-exit', errorMessage = '') => {
    if (isStartMenuOpen) {
      toggleStartMenu();
    }
    setScreensaverActive(false);
    setSelectedIconId(null);
    setLockError(errorMessage);
    setLockOverlayMode(mode);
  };

  const lockMapleOsNow = () => {
    if (!pinConfigured) {
      toast({
        title: 'Set a PIN first',
        description: 'Create a MapleOS PIN in Security Settings before using screen lock.',
        variant: 'destructive',
      });
      return;
    }

    openLockOverlay('screen');
  };

  // Helpers: grid snapping and available slot finder
  const snapToGrid = (coords: { x: number; y: number }) => {
    const iconWidth = 80;
    const iconHeight = 100;
    const margin = 20;
    const gridX = iconWidth + margin;
    const gridY = iconHeight + margin;

    const maxY = Math.max(0, (window.innerHeight - 150) - iconHeight); // leave taskbar space
    const maxX = Math.max(0, (window.innerWidth - 40) - iconWidth);

    let col = Math.max(0, Math.round((coords.x - margin) / gridX));
    let row = Math.max(0, Math.round((coords.y - margin) / gridY));

    const x = Math.min(margin + col * gridX, maxX);
    const y = Math.min(margin + row * gridY, maxY);
    return { x, y };
  };

  const isOccupied = (x: number, y: number, excludePath?: string) => {
    const threshold = 80;
    // Check current icons on screen
    const occupiedByIcons = desktopIcons.some((icon) => {
      if (excludePath && icon.filePath === excludePath) return false;
      const dx = icon.x - x;
      const dy = icon.y - y;
      return Math.sqrt(dx * dx + dy * dy) < threshold;
    });
    if (occupiedByIcons) return true;

    // Check saved positions map
    const positions = Object.entries(iconPositions);
    for (const [path, pos] of positions) {
      if (excludePath && path === excludePath) continue;
      const dx = pos.x - x;
      const dy = pos.y - y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold) return true;
    }
    return false;
  };

  const findAvailablePosition = () => {
    const iconWidth = 80;
    const iconHeight = 100;
    const margin = 20;
    const gridX = iconWidth + margin;
    const gridY = iconHeight + margin;

    const maxRows = Math.max(1, Math.floor((window.innerHeight - 150) / gridY));
    const maxCols = Math.max(1, Math.floor((window.innerWidth - 40) / gridX));

    for (let col = 0; col < maxCols; col++) {
      for (let row = 0; row < maxRows; row++) {
        const x = margin + col * gridX;
        const y = margin + row * gridY;
        if (!isOccupied(x, y)) {
          return { x, y };
        }
      }
    }
    return { x: 120, y: 20 };
  };

  const reserveIconPosition = (path: string, x: number, y: number) => {
    const snapped = snapToGrid({ x, y });
    setIconPositions((prev) => ({ ...prev, [path]: snapped }));
  };

  // Auto-trigger startup sound once the desktop mounts (after boot)
  useEffect(() => {
    // Skip if already enabled or already played this session
    const played = sessionStorage.getItem('MapleOS-startup-sound-played') === 'true';
    if (!soundLoaded) return;
    if (!soundSettings.startupEnabled) return;
    if (shouldPlayStartupSound || played) return;
    const t = setTimeout(() => setShouldPlayStartupSound(true), 300);
    return () => clearTimeout(t);
  }, [shouldPlayStartupSound, soundLoaded, soundSettings.startupEnabled]);

  const { contextMenu, showContextMenu, hideContextMenu, submenuState, setSubmenuState } = useContextMenu();
  const fullScreenShellOverlayActive = screensaverActive || showSplash || Boolean(lockOverlayMode) || shellOverlayActive;
  
  // Use the same global filesystem navigator as FileExplorer

  const navigator = globalNavigator;

  const refreshRecycleBinState = React.useCallback(async () => {
    try {
      await navigator.waitForInitialization();
      await navigator.syncWithVFS();
      const recycleBinItems = navigator.getItemsInPath(RECYCLE_BIN_PATH);
      setRecycleBinHasItems(recycleBinItems.length > 0);
    } catch (error) {
      console.error('Failed to refresh recycle bin state:', error);
    }
  }, [navigator]);

  useEffect(() => {
    const handleRecycleBinChanged = () => {
      void refreshRecycleBinState();
    };

    const handleFileSystemChanged = (event: Event) => {
      const { detail } = event as CustomEvent<{ path?: string }>;
      const changedPath = detail?.path;

      if (changedPath === RECYCLE_BIN_PATH || changedPath?.startsWith(`${RECYCLE_BIN_PATH}\\`)) {
        void refreshRecycleBinState();
      }
    };

    void refreshRecycleBinState();
    window.addEventListener('recycleBinChanged', handleRecycleBinChanged);
    window.addEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);

    return () => {
      window.removeEventListener('recycleBinChanged', handleRecycleBinChanged);
      window.removeEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);
    };
  }, [refreshRecycleBinState]);

  // Run boot scripts once per session (config.sys + autoexec.bat)
  useEffect(() => {
    const alreadyRan = localStorage.getItem('MapleOS-autoexec-ran') === 'true';
    if (alreadyRan) return;

    const effects = {
      setTheme: (dark: boolean) => {
        const root = (document.querySelector('.mapleos-root') as HTMLElement) || document.documentElement;
        if (dark) root.classList.add('dark'); else root.classList.remove('dark');
      },
      setWallpaper: (url: string) => {
        setCurrentBackground(prev => (prev === url ? prev : url));
      },
      notify: (title: string, message: string) => {
        window.dispatchEvent(new CustomEvent('operator:notify', { detail: { title, message } }));
      }
    };

    (async () => {
      try {
        await runBootScripts(navigator, effects);
        localStorage.setItem('MapleOS-autoexec-ran', 'true');
      } catch (e) {
        console.error('Boot scripts failed:', e);
      }
    })();
  }, []);

  // Handle splash screen
  const handleShowSplash = (show: boolean) => {
    console.log('Desktop: Setting showSplash to', show);
    setShowSplash(show);
  };

  const handleSplashComplete = () => {
    console.log('Desktop: Splash completed');
    setShowSplash(false);
    // Show the beautiful success popup instead of alert
    setTimeout(() => {
      setShowSuccessPopup(true);
      // Trigger startup sound once after splash completes
      setShouldPlayStartupSound(true);
    }, 500);
  };

  // Listen for new items created in File Explorer
  useEffect(() => {
    const handleDesktopItemCreated = (event: CustomEvent) => {
      const { detail } = event;

      // NEW: avoid duplicates if an icon for this path already exists
      if (detail.filePath) {
        const existing = desktopIcons.find((i) => i.filePath === detail.filePath);
        if (existing) {
          // Optionally update label or toggle edit mode on the existing icon
          updateDesktopIcon(existing.id, { label: detail.label ?? existing.label, isEditing: true });
          return;
        }
      }

      // Find available position for new icon
      const { x, y } = findAvailablePosition();

      addDesktopIcon({
        id: detail.id,
        appId: detail.appId,
        label: detail.label,
        icon: Folder,
        x,
        y,
        filePath: detail.filePath
      });

      // NEW: persist its position
      if (detail.filePath) {
        reserveIconPosition(detail.filePath, x, y);
      }
    };

    window.addEventListener('desktopItemCreated', handleDesktopItemCreated as EventListener);
    return () => {
      window.removeEventListener('desktopItemCreated', handleDesktopItemCreated as EventListener);
    };
  }, [addDesktopIcon, desktopIcons, updateDesktopIcon]);

  // Listen for opening specific MapleOS apps (e.g., bug-report)
  useEffect(() => {
    const handleOpenApp = (event: CustomEvent) => {
      const detail = event?.detail || {};
      const appId = detail.appId as string | undefined;
      const title = (detail.title as string | undefined) || 'Application';
      const filePath = detail.filePath as string | undefined;

      if (appId) {
        openWindow(appId, title, filePath);
      }
    };

    window.addEventListener('mapleos-open-app', handleOpenApp as unknown as EventListener);
    return () => {
      window.removeEventListener('mapleos-open-app', handleOpenApp as unknown as EventListener);
    };
  }, [openWindow]);

  // Listen for new items created in File Explorer
  useEffect(() => {
    const handleOpenModule = (event: CustomEvent) => {
      const detail = event?.detail || {};
      const path = detail.path as string | undefined;
      const title = (detail.title as string | undefined) || 'Module';

      if (path) {
        openWindow('canxp-module', title, path);
      }
    };

    window.addEventListener('mapleos-open-module', handleOpenModule as unknown as EventListener);
    return () => {
      window.removeEventListener('mapleos-open-module', handleOpenModule as unknown as EventListener);
    };
  }, [openWindow]);

  // Load display settings and apply them on startup
  useEffect(() => {
    console.log('Desktop: Loading display settings - displayLoaded:', displayLoaded, 'settings:', displaySettings);
    
    if (displayLoaded) {
      const root = (document.querySelector('.mapleos-root') as HTMLElement) || document.documentElement;

      // Apply dark mode
      if (displaySettings.isDarkMode) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      // Apply desktop solid color if provided (H S% L% string)
      if (displaySettings.desktopColor) {
        root.style.setProperty('--desktop', displaySettings.desktopColor);
      }

      // Apply accent colors
      if (displaySettings.selectedAccent) {
        // Accent color definitions (should match DisplayProperties)
        const accentColors = [
          {
            id: 'blue',
            colors: {
              light: { primary: '210 85% 45%', accent: '210 50% 85%' },
              dark: { primary: '210 85% 55%', accent: '210 75% 25%' }
            }
          },
          {
            id: 'purple',
            colors: {
              light: { primary: '270 90% 50%', accent: '270 50% 85%' },
              dark: { primary: '270 85% 60%', accent: '270 75% 25%' }
            }
          },
          {
            id: 'green',
            colors: {
              light: { primary: '160 84% 39%', accent: '160 50% 85%' },
              dark: { primary: '160 84% 49%', accent: '160 75% 25%' }
            }
          },
          {
            id: 'orange',
            colors: {
              light: { primary: '20 90% 50%', accent: '20 50% 85%' },
              dark: { primary: '20 90% 60%', accent: '20 75% 25%' }
            }
          },
          {
            id: 'red',
            colors: {
              light: { primary: '0 84% 60%', accent: '0 50% 85%' },
              dark: { primary: '0 84% 65%', accent: '0 75% 25%' }
            }
          },
          {
            id: 'teal',
            colors: {
              light: { primary: '180 84% 39%', accent: '180 50% 85%' },
              dark: { primary: '180 84% 49%', accent: '180 75% 25%' }
            }
          },
          // Futuristic Neon (includes MapleOS website accent)
          {
            id: 'neon-lime',
            colors: {
              light: { primary: '75 100% 42%', accent: '75 80% 90%' },
              dark: { primary: '75 100% 55%', accent: '75 90% 22%' }
            }
          },
          {
            id: 'neon-cyan',
            colors: {
              light: { primary: '190 100% 42%', accent: '190 85% 90%' },
              dark: { primary: '190 100% 58%', accent: '190 90% 22%' }
            }
          },
          {
            id: 'neon-magenta',
            colors: {
              light: { primary: '320 100% 52%', accent: '320 90% 92%' },
              dark: { primary: '320 100% 66%', accent: '320 85% 24%' }
            }
          },
          {
            id: 'neon-violet',
            colors: {
              light: { primary: '270 100% 55%', accent: '270 90% 92%' },
              dark: { primary: '270 100% 70%', accent: '270 85% 24%' }
            }
          },
          {
            id: 'neon-blue',
            colors: {
              light: { primary: '210 100% 52%', accent: '210 95% 92%' },
              dark: { primary: '210 100% 68%', accent: '210 90% 24%' }
            }
          },
          {
            id: 'neon-amber',
            colors: {
              light: { primary: '45 100% 50%', accent: '45 95% 90%' },
              dark: { primary: '45 100% 62%', accent: '45 90% 22%' }
            }
          }
        ];

        const parseLightness = (hsl: string): number | null => {
          const parts = (hsl || '').trim().split(/\s+/);
          if (parts.length < 3) return null;
          const lRaw = parts[2].replace('%', '');
          const l = Number.parseFloat(lRaw);
          return Number.isFinite(l) ? l : null;
        };

        const parseHsl = (hsl: string): { h: number; s: number; l: number } | null => {
          const parts = (hsl || '').trim().split(/\s+/);
          if (parts.length < 3) return null;
          const h = Number.parseFloat(parts[0]);
          const s = Number.parseFloat(parts[1].replace('%', '')) / 100;
          const l = Number.parseFloat(parts[2].replace('%', '')) / 100;
          if (![h, s, l].every(Number.isFinite)) return null;
          return { h: ((h % 360) + 360) % 360, s: Math.max(0, Math.min(1, s)), l: Math.max(0, Math.min(1, l)) };
        };

        const hslToRgb = (hsl: string): { r: number; g: number; b: number } | null => {
          const p = parseHsl(hsl);
          if (!p) return null;
          const { h, s, l } = p;
          const c = (1 - Math.abs(2 * l - 1)) * s;
          const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
          const m = l - c / 2;

          let r1 = 0;
          let g1 = 0;
          let b1 = 0;

          if (h < 60) {
            r1 = c;
            g1 = x;
          } else if (h < 120) {
            r1 = x;
            g1 = c;
          } else if (h < 180) {
            g1 = c;
            b1 = x;
          } else if (h < 240) {
            g1 = x;
            b1 = c;
          } else if (h < 300) {
            r1 = x;
            b1 = c;
          } else {
            r1 = c;
            b1 = x;
          }

          return {
            r: Math.max(0, Math.min(1, r1 + m)),
            g: Math.max(0, Math.min(1, g1 + m)),
            b: Math.max(0, Math.min(1, b1 + m)),
          };
        };

        const relativeLuminance = (hsl: string): number | null => {
          const rgb = hslToRgb(hsl);
          if (!rgb) return null;

          const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
          const r = lin(rgb.r);
          const g = lin(rgb.g);
          const b = lin(rgb.b);

          return 0.2126 * r + 0.7152 * g + 0.0722 * b;
        };

        const pickForeground = (bgHsl: string, mode: unknown, customHsl: unknown): string => {
          const DARK_TEXT = '220 20% 8%';
          const LIGHT_TEXT = '0 0% 100%';
          if (mode === 'light') return LIGHT_TEXT;
          if (mode === 'dark') return DARK_TEXT;
          if (mode === 'custom' && typeof customHsl === 'string' && customHsl.trim()) return customHsl;

          const lum = relativeLuminance(bgHsl);
          if (lum === null) return LIGHT_TEXT;
          return lum >= 0.58 ? DARK_TEXT : LIGHT_TEXT;
        };

        const savedAccent = accentColors.find(a => a.id === displaySettings.selectedAccent);
        if (savedAccent) {
          const mode = displaySettings.isDarkMode ? 'dark' : 'light';
          const colors = savedAccent.colors[mode];
          
          root.style.setProperty('--primary', colors.primary);
          root.style.setProperty('--accent', colors.accent);
          root.style.setProperty('--window-header', colors.primary);
          root.style.setProperty('--ring', colors.primary);

          const primaryFg = pickForeground(
            colors.primary,
            (displaySettings as any).primaryForegroundMode,
            (displaySettings as any).primaryForegroundCustom
          );
          const accentFg = pickForeground(
            colors.accent,
            (displaySettings as any).accentForegroundMode,
            (displaySettings as any).accentForegroundCustom
          );
          root.style.setProperty('--primary-foreground', primaryFg);
          root.style.setProperty('--accent-foreground', accentFg);

          const useAccentTitlebar = (displaySettings as any).useAccentTitlebar !== false;
          if (useAccentTitlebar) {
            root.style.setProperty('--window-header', colors.primary);
            root.style.setProperty('--window-header-foreground', primaryFg);
          } else {
            root.style.setProperty('--window-header', 'var(--card)');
            root.style.setProperty('--window-header-foreground', 'var(--foreground)');
          }

          const headerL = useAccentTitlebar ? (parseLightness(colors.primary) ?? 0) : (mode === 'dark' ? 12 : 92);
          const brightHeader = headerL >= 60;
          root.style.setProperty(
            '--window-control-bg',
            brightHeader ? 'rgba(0, 0, 0, 0.10)' : 'rgba(255, 255, 255, 0.12)'
          );
          root.style.setProperty(
            '--window-control-bg-hover',
            brightHeader ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.18)'
          );
          
          // Update gradient
          const primaryHsl = `hsl(${colors.primary})`;
          const darkerPrimary = colors.primary.replace(/(\d+)%\)/, (match, lightness) => {
            const newLightness = Math.max(10, parseInt(lightness) - 8);
            return `${newLightness}%)`;
          });
          const darkerPrimaryHsl = `hsl(${darkerPrimary})`;
          root.style.setProperty(
            '--gradient-window',
            useAccentTitlebar
              ? `linear-gradient(180deg, ${primaryHsl}, ${darkerPrimaryHsl})`
              : 'linear-gradient(180deg, hsl(var(--card)), hsl(var(--muted)))'
          );
          
          console.log('Desktop: Applied accent color:', displaySettings.selectedAccent, mode);
        }
      }
      
      // Apply wallpaper (will be overridden by desktopConfig below if present)
      if (displaySettings.selectedBackground) {
        // If user selected 'none', keep image disabled
        if (displaySettings.selectedBackground === 'none') {
          setCurrentBackground(prev => (prev !== '' ? '' : prev));
        } else {
          // Wallpaper definitions (should match DisplayProperties)
          const wallpapers = [
            { id: 'default', name: 'Classic Maple', url: '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg' },
            { id: 'edmonton', name: 'Edmonton Nights', url: '/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg' },
            { id: 'owl', name: 'Snow Owl', url: '/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg' },
            { id: 'toronto', name: 'Toronto Sky', url: '/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg' },
            { id: 'deer', name: 'Deer', url: '/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg' },
            { id: 'cubes', name: '3D Cubes', url: '/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg' },
          ];

          const savedWallpaper = wallpapers.find(w => w.id === displaySettings.selectedBackground);
          if (savedWallpaper) {
            setCurrentBackground(prev => (prev === savedWallpaper.url ? prev : savedWallpaper.url));
          }
        }
      }
      
      // Apply desktop icon customization
      if (displaySettings.iconTextColor && displaySettings.iconTextColor !== 'auto') {
        console.log('Desktop: Applying icon customization:', {
          iconTextColor: displaySettings.iconTextColor,
          customIconTextColor: displaySettings.customIconTextColor,
          iconBackgroundOpacity: displaySettings.iconBackgroundOpacity
        });
        
        // Apply icon text color based on selection
        let textColor = '';
        switch (displaySettings.iconTextColor) {
          case 'light':
            textColor = '255 255 255';
            break;
          case 'dark':
            textColor = '0 0 0';
            break;
          case 'custom':
            textColor = displaySettings.customIconTextColor || '255 255 255';
            break;
          case 'auto':
          default:
            // Use theme default (no override)
            root.style.removeProperty('--desktop-icon-text-color');
            root.style.removeProperty('--desktop-icon-bg-opacity');
            break;
        }
        
        if (textColor) {
          root.style.setProperty('--desktop-icon-text-color', `rgb(${textColor})`);
          root.style.setProperty('--desktop-icon-bg-opacity', (displaySettings.iconBackgroundOpacity !== undefined ? displaySettings.iconBackgroundOpacity : 0.5).toString());
          console.log('Desktop: Applied icon styles - color:', textColor, 'opacity:', displaySettings.iconBackgroundOpacity);
        }
      } else {
        // Remove custom icon styles if iconTextColor is auto or not set
        const root = (document.querySelector('.mapleos-root') as HTMLElement) || document.documentElement;
        root.style.removeProperty('--desktop-icon-text-color');
        root.style.removeProperty('--desktop-icon-bg-opacity');
        console.log('Desktop: Removed custom icon styles (using auto)');
      }
    } else {
      console.log('Desktop: Display settings not loaded yet');
    }
  }, [displayLoaded, displaySettings.selectedBackground, displaySettings.selectedAccent, displaySettings.isDarkMode, displaySettings.iconTextColor, displaySettings.customIconTextColor, displaySettings.iconBackgroundOpacity, displaySettings.desktopColor]);

  // Prefer exact backgroundUrl from user desktop config if available (ensures wallpaper persists across reopening)
  useEffect(() => {
    if (desktopConfigLoaded) {
      const bgUrl = desktopConfig?.backgroundUrl;
      // If bgUrl is an empty string or falsy, disable the image
      if (!bgUrl) {
        setCurrentBackground(prev => (prev !== '' ? '' : prev));
      } else {
        setCurrentBackground(prev => (prev === bgUrl ? prev : bgUrl));
      }
    }
  }, [desktopConfigLoaded, desktopConfig?.backgroundUrl]);

  // Apply desktop color from desktop config if present
  useEffect(() => {
    if (desktopConfigLoaded && desktopConfig?.desktopColor) {
      const root = (document.querySelector('.mapleos-root') as HTMLElement) || document.documentElement;
      root.style.setProperty('--desktop', desktopConfig.desktopColor as string);
    }
  }, [desktopConfigLoaded, desktopConfig?.desktopColor]);

  // Resolve the current user's Desktop path (fallback to 'User')
  const getUserDesktopPath = React.useCallback(() => {
    const raw = (profile?.username || user?.email?.split('@')[0] || 'User') || 'User';
    const username = raw.replace(/[^a-zA-Z0-9]/g, '') || 'User';
    return `C:\\User\\${username}\\Desktop`;
  }, [user, profile]);

  // Helper: route apps by extension
  const getAppIdForItem = (name: string, isFolder: boolean) => {
    if (isFolder) return 'file-explorer';
    const ext = (name.split('.').pop() || '').toLowerCase();

    if (ext === 'mword' || ext === 'html') return 'maple-word';
    if (ext === 'mslide' || ext === 'mapleslide') return 'maple-slide';
    if (ext === 'msheet' || ext === 'sheet') return 'maple-sheet';
    if (ext === 'mmoney' || ext === 'mmny') return 'maple-money';
    if (ext === 'mseed') return 'maple-seed-vault';
    if (['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)) return 'image-viewer';

    if (['mp3','wav','flac','ogg','aac','m4a'].includes(ext)) return 'music-player';
    if (['bat','cmd','sh'].includes(ext)) return 'terminal';
    // default text-like → Notepad
    return 'notepad';
  };

  const getDesktopIconComponentForItem = (name: string, isFolder: boolean) => {
    if (isFolder) {
      return Folder;
    }

    const ext = (name.split('.').pop() || '').toLowerCase();
    if (ext === 'mseed') {
      return MapleSeedDesktopFileIcon;
    }

    if (ext === 'txt') {
      return DesktopTextFileIcon;
    }

    return FileText;
  };

  // Load and sync desktop icons with user's Desktop contents
  useEffect(() => {
    let isMounted = true;

    const syncDesktopWithFolder = async () => {
      try {
        await navigator.waitForInitialization();
        const desktopPath = getUserDesktopPath();
        const desktopItems = navigator.getItemsInPath(desktopPath);
        if (!isMounted) return;

        // Remove any icons backed by filesystem items within the Desktop path (dedupe)
        const iconsToRemove = iconsRef.current.filter(
          (icon) => icon.filePath && icon.filePath.startsWith(desktopPath)
        );
        iconsToRemove.forEach((icon) => removeDesktopIcon(icon.id));

        // Add icons using saved positions when available
        desktopItems.forEach((item) => {
          const savedPos = iconPositions[item.path];
          const pos = savedPos || findAvailablePosition();

          addDesktopIcon({
            id: `desktop-file-${item.id}`,
            appId: getAppIdForItem(item.name, item.type === 'folder'),
            label: item.name,
            icon: getDesktopIconComponentForItem(item.name, item.type === 'folder'),
            x: pos.x,
            y: pos.y,
            filePath: item.path
          });

          if (!savedPos) {
            reserveIconPosition(item.path, pos.x, pos.y);
          }
        });
      } catch (error) {
        console.error('Failed to sync desktop with folder:', error);
      }
    };

    syncDesktopWithFolder();
    return () => { isMounted = false; };
  }, [navigator, addDesktopIcon, removeDesktopIcon, getUserDesktopPath]);

  // Listen for filesystem changes and re-sync desktop for the user's Desktop path
  useEffect(() => {
    let resyncTimeout: number | undefined;

    const handler = (event: CustomEvent) => {
      const desktopPath = getUserDesktopPath();
      const { detail } = event;
      if (detail.path === desktopPath) {
        const resync = async () => {
          try {
            const desktopItems = navigator.getItemsInPath(desktopPath);

            // Remove any icons backed by filesystem items within the Desktop path (dedupe)
            const iconsToRemove = iconsRef.current.filter(
              (icon) => icon.filePath && icon.filePath.startsWith(desktopPath)
            );
            iconsToRemove.forEach((icon) => removeDesktopIcon(icon.id));

            // Read latest saved positions snapshot to avoid stale closure
            let savedPositions: Record<string, { x: number; y: number }> = {};
            try {
              const raw = localStorage.getItem(ICON_POS_KEY);
              savedPositions = raw ? JSON.parse(raw) : {};
            } catch {
              savedPositions = {};
            }

            // Re-add current items using saved positions when available
            desktopItems.forEach((item) => {
              const savedPos = savedPositions[item.path];
              const pos = savedPos || findAvailablePosition();

              // Skip adding if an icon for this path already exists
              const existing = iconsRef.current.find((i) => i.filePath === item.path);
              if (existing) {
                // Optionally refresh label/appId if needed, but keep existing position
                return;
              }

              addDesktopIcon({
                id: `desktop-file-${item.id}`,
                appId: getAppIdForItem(item.name, item.type === 'folder'),
                label: item.name,
                icon: getDesktopIconComponentForItem(item.name, item.type === 'folder'),
                x: pos.x,
                y: pos.y,
                filePath: item.path
              });

              if (!savedPos) {
                reserveIconPosition(item.path, pos.x, pos.y);
              }
            });
          } catch (error) {
            console.error('Failed to re-sync desktop with folder:', error);
          }
        };

        if (resyncTimeout) clearTimeout(resyncTimeout);
        resyncTimeout = window.setTimeout(resync, 150);
      }
    };

    window.addEventListener('fileSystemChanged', handler as EventListener);
    return () => {
      if (resyncTimeout) clearTimeout(resyncTimeout);
      window.removeEventListener('fileSystemChanged', handler as EventListener);
    };
  }, [navigator, getUserDesktopPath]);

  // Listen for assistant requests to arrange icons
  useEffect(() => {
    const handler = (ev: Event) => {
      const custom = ev as CustomEvent<{ sortBy: 'name' | 'date' }>;
      const sortBy = custom.detail?.sortBy || 'name';
      arrangeDesktopIcons(sortBy);
    };
    window.addEventListener('desktop:arrangeIcons', handler as EventListener);
    return () => window.removeEventListener('desktop:arrangeIcons', handler as EventListener);
  }, [arrangeDesktopIcons, desktopIcons]);

  // Listen for assistant wallpaper changes
  useEffect(() => {
    const handler = (ev: Event) => {
      const custom = ev as CustomEvent<{ url: string }>;
      const url = custom.detail?.url;
      if (url) {
        setCurrentBackground((prev) => prev === url ? prev : url);
      }
    };
    window.addEventListener('mapleos:setWallpaper', handler as EventListener);
    return () => window.removeEventListener('mapleos:setWallpaper', handler as EventListener);
  }, []);

  // Screensaver logic
  useEffect(() => {
    // Resolve duration (in minutes) from settings; fallback to 5 min
    const durationMinutes =
      displayLoaded && typeof displaySettings.screensaverDuration === 'number'
        ? displaySettings.screensaverDuration
        : 5;

    const thresholdMs = Math.max(1, durationMinutes) * 60 * 1000;

    const checkScreensaver = () => {
      // If screensaver type is 'none', ensure it's disabled
      if (displayLoaded && displaySettings.screensaverType === 'none') {
        if (screensaverActive) setScreensaverActive(false);
        return;
      }

      if (gameWindowActive || lockOverlayMode === 'screen') {
        if (screensaverActive) {
          setScreensaverActive(false);
        }
        return;
      }

      const now = Date.now();
      if (now - lastActivity >= thresholdMs) {
        setScreensaverActive(true);
      }
    };

    const interval = setInterval(checkScreensaver, 1000);
    return () => clearInterval(interval);
  }, [
    lastActivity,
    displayLoaded,
    displaySettings.screensaverDuration,
    displaySettings.screensaverType,
    screensaverActive,
    gameWindowActive,
    lockOverlayMode
  ]);

  const handleActivity = () => {
    if (screensaverActive) {
      setScreensaverActive(false);
    }
    updateActivity();
  };

  // Activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [screensaverActive]);

  useEffect(() => {
    if (!securityLoaded || !pinConfigured || !securitySettings.autoLockEnabled || lockOverlayMode !== null) {
      return;
    }

    const thresholdMs = Math.max(1, Number(securitySettings.autoLockMinutes || 15)) * 60 * 1000;
    const interval = window.setInterval(() => {
      if (gameWindowActive) {
        return;
      }

      if (Date.now() - lastActivity >= thresholdMs) {
        lockMapleOsNow();
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [
    securityLoaded,
    pinConfigured,
    securitySettings.autoLockEnabled,
    securitySettings.autoLockMinutes,
    lockOverlayMode,
    gameWindowActive,
    lastActivity,
  ]);

  useEffect(() => {
    const handler = () => {
      lockMapleOsNow();
    };

    window.addEventListener('mapleos:lock-screen', handler as EventListener);
    return () => window.removeEventListener('mapleos:lock-screen', handler as EventListener);
  }, [pinConfigured, securityLoaded, isStartMenuOpen]);

  useEffect(() => {
    if (!isElectron) {
      return;
    }

    let cancelled = false;

    const syncFullscreenState = async () => {
      try {
        const fullscreen = await window.electronAPI!.windowIsFullScreen();
        if (cancelled) {
          return;
        }

        const previousFullscreen = previousElectronFullScreenRef.current;
        setElectronFullScreen(fullscreen);

        if (fullscreen && !previousFullscreen && fullscreenExitBypassRef.current) {
          fullscreenExitBypassRef.current = false;
        }

        if (
          !fullscreen &&
          previousFullscreen &&
          securityLoaded &&
          pinConfigured &&
          securitySettings.fullscreenPinLockEnabled &&
          !fullscreenExitBypassRef.current
        ) {
          await window.electronAPI?.windowFullscreen();
          if (!cancelled) {
            setLockOverlayMode((current) => current ?? 'fullscreen-exit');
            setLockError('Enter the MapleOS PIN to leave full screen mode.');
          }
          return;
        }

        previousElectronFullScreenRef.current = fullscreen;
      } catch (error) {
        console.error('Failed to sync Electron fullscreen state:', error);
      }
    };

    void syncFullscreenState();
    const interval = window.setInterval(() => {
      void syncFullscreenState();
    }, 400);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    isElectron,
    securityLoaded,
    pinConfigured,
    securitySettings.fullscreenPinLockEnabled,
  ]);

  useEffect(() => {
    if (!isElectron || !securityLoaded || !pinConfigured || !securitySettings.fullscreenPinLockEnabled) {
      return;
    }

    if (!electronFullScreen && !fullscreenExitBypassRef.current) {
      void window.electronAPI?.windowFullscreen();
    }
  }, [
    electronFullScreen,
    isElectron,
    pinConfigured,
    securityLoaded,
    securitySettings.fullscreenPinLockEnabled,
  ]);

  useEffect(() => {
    if (!isElectron || !securityLoaded || !pinConfigured || !securitySettings.fullscreenPinLockEnabled) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'F11' && electronFullScreen && !fullscreenExitBypassRef.current) {
        event.preventDefault();
        event.stopPropagation();
        openLockOverlay('fullscreen-exit');
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [
    electronFullScreen,
    isElectron,
    pinConfigured,
    securityLoaded,
    securitySettings.fullscreenPinLockEnabled,
    isStartMenuOpen,
  ]);

  const handleSecurityOverlaySubmit = async (pin: string) => {
    const validPin = await verifyStoredPin(pin, securitySettings);
    if (!validPin) {
      setLockError('Incorrect PIN.');
      return;
    }

    setLockError('');

    if (lockOverlayMode === 'fullscreen-exit') {
      fullscreenExitBypassRef.current = true;
      setLockOverlayMode(null);
      if (electronFullScreen && isElectron) {
        await window.electronAPI?.windowFullscreen();
      }
      toast({
        title: 'Full screen unlocked',
        description: 'MapleOS will allow this full screen exit.',
      });
      return;
    }

    setLockOverlayMode(null);
    updateActivity();
    toast({ title: 'MapleOS unlocked' });
  };

  const handleSecurityOverlayCancel = () => {
    setLockError('');
    setLockOverlayMode(null);
  };

  // Screensaver will be rendered as overlay instead of replacing content

  const handleDesktopClick = (e: React.MouseEvent) => {
    hideContextMenu();
    setSelectedIconId(null); // Clear icon selection when clicking on desktop
    
    // Close start menu if clicking outside of it
    if (isStartMenuOpen) {
      const target = e.target as HTMLElement;
      // Check if the click is not on the start menu itself or the start button
      const isStartMenuClick = target.closest('[data-start-menu]') || target.closest('[data-start-button]');
      
      if (!isStartMenuClick) {
        toggleStartMenu();
      }
    }
  };

  const handleDesktopRightClick = (e: React.MouseEvent) => {
    console.log('Desktop right-click detected');
    console.log('Event details:', e.type, e.clientX, e.clientY);
    e.preventDefault();
    const desktopContextItems: ContextMenuItem[] = [
      {
        id: 'new',
        label: 'New',
        icon: <NewMenuIcon size={16} className="h-4 w-4 text-current" />,
        submenu: [
          {
            id: 'new-folder',
            label: 'Folder',
            icon: <NewFolderMenuIcon size={16} className="h-4 w-4 text-current" />,
            onClick: async () => {
              try {
                await navigator.waitForInitialization();
                const newFolderName = `New Folder`;
                const desktopPath = getUserDesktopPath();
                const newFolderPath = `${desktopPath}\\${newFolderName}`;

                const success = await navigator.createFolder(newFolderPath);
                if (success) {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('fileSystemChanged', {
                      detail: { path: desktopPath }
                    }));
                  }, 500);

                  // Find an available position on desktop (unchanged logic)
                  const findAvailablePosition = () => {
                    const iconWidth = 80;
                    const iconHeight = 100;
                    const margin = 20;
                    const gridX = iconWidth + margin;
                    const gridY = iconHeight + margin;
                    for (let row = 0; row < 10; row++) {
                      for (let col = 1; col < 15; col++) {
                        const x = margin + (col * gridX);
                        const y = margin + (row * gridY);
                        const overlaps = desktopIcons.some(icon => {
                          const distance = Math.sqrt(Math.pow(icon.x - x, 2) + Math.pow(icon.y - y, 2));
                          return distance < iconWidth;
                        });
                        if (!overlaps) return { x, y };
                      }
                    }
                    return { x: 120, y: 20 };
                  };

                  const { x: availableX, y: availableY } = findAvailablePosition();
                  const newFolderIcon = {
                    id: `desktop-file-${Date.now()}`,
                    appId: 'file-explorer',
                    label: newFolderName,
                    icon: Folder,
                    x: availableX,
                    y: availableY,
                    filePath: newFolderPath,
                    isEditing: true
                  };

                  addDesktopIcon(newFolderIcon);
                  setSelectedIconId(newFolderIcon.id);
                }
              } catch (error) {
                console.error('Error creating folder:', error);
              }
            }
          },
          {
            id: 'new-text',
            label: 'Text Document',
            icon: <NewTextDocumentMenuIcon size={16} className="h-4 w-4 text-current" />,
            onClick: async () => {
              const newFileName = `New Text Document ${Date.now() % 1000}.txt`;
              const desktopPath = getUserDesktopPath();
              const newFilePath = `${desktopPath}\\${newFileName}`;
              try {
                const success = await navigator.createFile(newFilePath, '');
                if (success) {
                  openWindow('notepad', `Notepad - ${newFileName}`, newFilePath, '');
                }
              } catch (error) {
                console.error('Error creating file:', error);
              }
            }
          }
        ]
      },
      {
        id: 'separator1',
        label: '',
        separator: true
      },
      {
        id: 'arrange-icons',
        label: 'Arrange Desktop Icons',
        icon: <ArrangeDesktopIconsMenuIcon size={16} className="h-4 w-4 text-current" />,
        submenu: [
          {
            id: 'arrange-by-name',
            label: 'By Name',
            onClick: () => {
              arrangeDesktopIcons('name');
            }
          },
          {
            id: 'arrange-by-date',
            label: 'By Date Created',
            onClick: () => {
              arrangeDesktopIcons('date');
            }
          }
        ]
      },
      {
        id: 'separator2',
        label: '',
        separator: true
      },
      {
        id: 'display-settings',
        label: 'Display settings',
        icon: <DisplaySettingsMenuIcon size={16} className="h-4 w-4 text-current" />,
        onClick: () => {
          openWindow('display-properties', 'Display Properties');
        }
      },
      {
        id: 'personalise',
        label: 'Personalise',
        icon: <PersonaliseMenuIcon size={16} className="h-4 w-4 text-current" />,
        onClick: () => {
          openWindow('display-properties', 'Display Properties');
        }
      }
    ];

    console.log('Showing context menu with items:', desktopContextItems);
    showContextMenu(e, desktopContextItems);
  };

  const handleEmptyRecycleBin = async () => {
    try {
      await navigator.waitForInitialization();
      const success = await navigator.emptyRecycleBin();

      if (success) {
        toast({
          title: 'Recycle Bin emptied',
          description: 'All deleted items were permanently removed.',
        });
      } else {
        toast({
          title: 'Recycle Bin not emptied',
          description: 'There was a problem removing deleted items.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to empty recycle bin:', error);
      toast({
        title: 'Recycle Bin not emptied',
        description: 'There was a problem removing deleted items.',
        variant: 'destructive',
      });
    }
  };

  const handleBackgroundChange = (newBackground: string) => {
    // Allow empty to represent "no wallpaper"
    setCurrentBackground(newBackground || '');
  };

  const handleIconRename = async (iconId: string, newName: string) => {
    const iconToRename = desktopIcons.find(icon => icon.id === iconId);
    
    // If this is just triggering edit mode (same name), set editing state
    if (iconToRename && newName === iconToRename.label) {
      updateDesktopIcon(iconId, { isEditing: true });
      return;
    }
    
    if (iconToRename && iconToRename.filePath) {
      // Update filesystem
      const oldPath = iconToRename.filePath;
      const newPath = oldPath.replace(/[^\\]+$/, newName);
      
      console.log('Renaming from', oldPath, 'to', newPath);
      
      try {
        // Get the item before we try to rename it
        const item = navigator.findItemByPath(oldPath);
        if (item) {
          if (item.type === 'folder') {
            await navigator.createFolder(newPath);
          } else {
            await navigator.createFile(newPath, item.content || '');
          }
          
          // Delete the old path
          await navigator.deleteItem(oldPath);
          console.log('Removed old path from filesystem:', oldPath);
        }
        
        // Update the icon
        updateDesktopIcon(iconId, { 
          label: newName, 
          filePath: newPath 
        });

        // NEW: migrate saved position to the new path
        setIconPositions(prev => {
          const next = { ...prev };
          if (iconToRename?.filePath && next[iconToRename.filePath]) {
            next[newPath] = next[iconToRename.filePath];
            delete next[iconToRename.filePath];
          }
          return next;
        });
        
        // Notify File Explorer of the filesystem change
        const desktopPath = getUserDesktopPath();
        window.dispatchEvent(new CustomEvent('fileSystemChanged', {
          detail: { path: desktopPath }
        }));
        
      } catch (error) {
        console.error('Failed to rename:', error);
      }
    } else {
      // Just update the label for non-file icons
      updateDesktopIcon(iconId, { label: newName });
    }
  };

  const handleIconDelete = async (iconId: string) => {
    const iconToDelete = desktopIcons.find(icon => icon.id === iconId);
    if (iconToDelete && iconToDelete.filePath) {
      try {
        // Delete from filesystem
        const success = await navigator.deleteItem(iconToDelete.filePath);
        if (success) {
          // Remove desktop icon
          removeDesktopIcon(iconId);

          // NEW: remove saved position
          if (iconToDelete?.filePath) {
            setIconPositions(prev => {
              const next = { ...prev };
              delete next[iconToDelete.filePath];
              return next;
            });
          }
          
          // Notify File Explorer of the filesystem change
          const desktopPath = getUserDesktopPath();
          window.dispatchEvent(new CustomEvent('fileSystemChanged', {
            detail: { path: desktopPath }
          }));
        }
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    } else {
      // Just remove the icon for non-file icons
      removeDesktopIcon(iconId);
    }
  };

  const handleFinishEditing = (iconId: string) => {
    updateDesktopIcon(iconId, { isEditing: false });
  };

  useEffect(() => {
    const handleDesktopKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      if (isTyping || activeWindow || !selectedIconId) {
        return;
      }

      const selectedIcon = desktopIcons.find((icon) => icon.id === selectedIconId);
      if (!selectedIcon || selectedIcon.isEditing || !selectedIcon.filePath) {
        return;
      }

      if (e.key === 'Delete') {
        e.preventDefault();
        void handleIconDelete(selectedIcon.id);
      }
    };

    document.addEventListener('keydown', handleDesktopKeyDown);
    return () => document.removeEventListener('keydown', handleDesktopKeyDown);
  }, [activeWindow, desktopIcons, selectedIconId]);

  function arrangeDesktopIcons(sortBy: 'name' | 'date') {

    const iconWidth = 80;
    const iconHeight = 100;
    const margin = 20;
    const gridX = iconWidth + margin;
    const gridY = iconHeight + margin;

    // Sort icons based on the selected criteria
    const sortedIcons = [...desktopIcons].sort((a, b) => {
      if (sortBy === 'name') {
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      } else {
        // For date, we'll use the icon id which contains timestamp for new items
        // For existing items, we'll use their current order
        const aTime = a.id.includes('-') ? parseInt(a.id.split('-').pop() || '0') : 0;
        const bTime = b.id.includes('-') ? parseInt(b.id.split('-').pop() || '0') : 0;
        return aTime - bTime;
      }
    });

    // Arrange icons in a grid pattern - fill columns vertically first
    let row = 0;
    let col = 0;
    const maxRows = Math.floor((window.innerHeight - 150) / gridY); // Leave space for taskbar

    sortedIcons.forEach((icon) => {
      const x = margin + (col * gridX);
      const y = margin + (row * gridY);

      updateDesktopIconPosition(icon.id, x, y);

      row++;
      if (row >= maxRows) {
        row = 0;
        col++;
      }
    });
  }

  // Global hotkeys for full desktop screenshot
  useEffect(() => {
    const handler = async (e: KeyboardEvent) => {
      // Don't trigger while typing in inputs/textareas or contenteditable
      const target = e.target as HTMLElement | null;
      const isTyping =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (isTyping) return;

      const isPrintScreen = e.key === 'PrintScreen';
      const isAltShiftS = e.altKey && e.shiftKey && (e.code === 'KeyS' || e.key.toLowerCase() === 's');

      // Keep previous combos as best-effort; they may be intercepted by OS
      const isNumber3 = e.code === 'Digit3' || e.code === 'Numpad3' || e.key === '3';
      const isCtrlShift3 = e.ctrlKey && e.shiftKey && isNumber3;
      const isCmdShift3 = e.metaKey && e.shiftKey && isNumber3;

      if (isAltShiftS || isPrintScreen || isCtrlShift3 || isCmdShift3) {
        e.preventDefault();
        e.stopPropagation();

        try {
          const pngDataUrl = await captureDesktopToPng();
          const saved = await saveScreenshotToVFS(pngDataUrl, 'desktop');

          toast({
            title: 'Screenshot captured',
            description: 'Saved to Pictures\\Screenshots.',
          });

          window.dispatchEvent(new CustomEvent('fileSystemChanged', {
            detail: { path: saved.vfsPath }
          }));
        } catch {
          toast({
            title: 'Capture failed',
            description: 'Could not capture the desktop.',
            variant: 'destructive',
          });
        }
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [toast]);

  const handleDesktopDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const types = e.dataTransfer.types || [];
    // Only respond to drags originating from File Explorer with our custom type
    if (!types.includes('application/maple-path')) {
      return; // Do not call preventDefault; let native/icon dragging work normally
    }
    e.preventDefault();
    if (!isDragOverDesktop) {
      setIsDragOverDesktop(true);
    }
  };

  const handleDesktopDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOverDesktop(false);

    const srcPath = e.dataTransfer.getData('application/maple-path');
    if (!srcPath) return;

    try {
      await navigator.waitForInitialization();
      const desktopPath = getUserDesktopPath();

      const name = srcPath.split('\\').pop() || 'item';
      let destPath = `${desktopPath}\\${name}`;

      if (navigator.pathExists(destPath)) {
        let counter = 2;
        const base = name.replace(/\.[^.\s]+$/, '');
        const extMatch = name.match(/(\.[^.\s]+)$/);
        const ext = extMatch ? extMatch[1] : '';
        destPath = `${desktopPath}\\${base} - Copy${ext}`;
        while (navigator.pathExists(destPath)) {
          destPath = `${desktopPath}\\${base} (${counter})${ext}`;
          counter++;
        }
      }

      const ok = await navigator.renameItem(srcPath, destPath);
      if (ok) {
        // NEW: place icon near drop point (snapped to grid) and persist it
        const rect = desktopRef.current?.getBoundingClientRect();
        if (rect) {
          const localX = e.clientX - rect.left;
          const localY = e.clientY - rect.top;
          const snapped = snapToGrid({ x: localX, y: localY });

          // If occupied, find next free slot
          const pos = isOccupied(snapped.x, snapped.y) ? findAvailablePosition() : snapped;
          reserveIconPosition(destPath, pos.x, pos.y);
        }

        window.dispatchEvent(
          new CustomEvent('fileSystemChanged', { detail: { path: desktopPath } })
        );
      }
    } catch (err) {
      console.error('Desktop drop move failed:', err);
    }
  };

  const handleDesktopDragLeave = () => {
    if (isDragOverDesktop) {
      setIsDragOverDesktop(false);
    }
  };

  return (
    <div
      ref={desktopRef}
      className={`w-full bg-desktop overflow-hidden relative select-none flex flex-col ${isDragOverDesktop ? 'ring-2 ring-primary/40' : ''}`}
      style={{
        height: '100dvh',
        paddingTop: desktopTopInset,
        boxSizing: 'border-box',
      }}
      onClick={handleDesktopClick}
      onContextMenu={handleDesktopRightClick}
      onDragOver={handleDesktopDragOver}
      onDrop={handleDesktopDrop}
      onDragLeave={handleDesktopDragLeave}
    >
      {/* Desktop Background */}
      <div 
        className="desktop-background absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-500"
        style={{
          backgroundImage: currentBackground ? `url(${getAssetPath(currentBackground)})` : 'none',
          backgroundColor: 'hsl(var(--desktop))'
        }}
      />

      {/* Top AI Taskbar */}
      {!hideAiTaskbar && (
        <div
          className="relative z-40 flex-shrink-0 overflow-visible transition-[height] duration-300"
          style={{ height: aiTaskbarPinned ? 48 : 0 }}
        >
          <DesktopAssistant onOpenWindow={openWindow} onPinnedChange={setAiTaskbarPinned} />
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        {/* Desktop Icons */}
        <div className="absolute inset-0 p-4">
          {desktopIcons.map((icon) => (
            <DesktopIcon
              key={icon.id}
              icon={icon}
              isSelected={selectedIconId === icon.id}
              onSelect={() => setSelectedIconId(icon.id)}
              onDoubleClick={() => {
                const path = icon.filePath;
                if (path) {
                  const item = navigator.findItemByPath(path);
                  const content = item?.content ?? '';
                  openWindow(icon.appId, icon.label, path, content);
                } else {
                  openWindow(icon.appId, icon.label);
                }
              }}
              onMove={(x, y) => updateDesktopIconPosition(icon.id, x, y)}
              onOpenWindow={openWindow}
              onRename={handleIconRename}
              onFinishEditing={handleFinishEditing}
              onDelete={handleIconDelete}
              onEmptyRecycleBin={icon.id === 'recycle-bin' ? handleEmptyRecycleBin : undefined}
              canEmptyRecycleBin={icon.id === 'recycle-bin' ? recycleBinHasItems : undefined}
            />
          ))}
        </div>

        <SystemMonitorWidget onOpenTaskManager={() => openWindow('task-manager', 'Task Manager')} />

        {/* Windows */}
        <WindowManager
          windows={visibleOpenWindows}
          fullScreenShellOverlayActive={fullScreenShellOverlayActive}
          startMenuOpen={isStartMenuOpen}
          contextMenuVisible={contextMenu.isVisible}
          onClose={closeWindow}
          onMinimize={minimizeWindow}
          onMaximize={maximizeWindow}
          onBoundsChange={updateWindowBounds}
          onFocus={focusWindow}
          onOpenWindow={openWindow}
          onBackgroundChange={handleBackgroundChange}
          onShowSplash={handleShowSplash}
        />

        {/* Start Menu */}
        {isStartMenuOpen && (
          <div data-start-menu>
            <StartMenu 
              onClose={() => toggleStartMenu()}
              onOpenApp={(appId, title) => {
                openWindow(appId, title);
              }}
            />
          </div>
        )}
      </div>

      {/* Taskbar */}
      <Taskbar 
        openWindows={visibleOpenWindows}
        onWindowClick={focusWindow}
        onStartClick={toggleStartMenu}
        onOpenWindow={openWindow}
        isStartMenuOpen={isStartMenuOpen}
        onToggleMinimize={minimizeWindow}
        groupButtons={Boolean(desktopConfig?.taskbarGroupButtons)}
        iconsOnly={Boolean(desktopConfig?.taskbarIconsOnly)}
        messengerTrayWindow={messengerTrayWindow}
        onMessengerTrayClick={handleMessengerTrayClick}
      />

      <AICreditAlertDialog onOpenWindow={openWindow} />

      {/* Context Menu */}
      <ContextMenu
        items={contextMenu.items}
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.isVisible}
        onClose={hideContextMenu}
        submenuState={submenuState}
        setSubmenuState={setSubmenuState}
      />

      {/* Splash Screen */}
      <SplashScreen 
        isVisible={showSplash} 
        onComplete={handleSplashComplete}
      />

      {/* Success Popup */}
      <ResetSuccessPopup
        isVisible={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
      />

      {/* Startup Sound (plays once per session after splash) */}
      <StartupSound
        enabled={shouldPlayStartupSound}
        src={soundSettings.startupSoundUrl || desktopConfig?.startupSoundUrl || '/os/assets/audio/startup.mp3'}
        volume={getEffectiveVolume(soundSettings.masterVolume, soundSettings.systemVolume, soundSettings.isMuted)}
      />

      {/* Screensaver Overlay */}
      {screensaverActive && (
        <div className="absolute inset-0 z-[9999]">
          <Screensaver onActivity={handleActivity} />
        </div>
      )}

      {lockOverlayMode && pinConfigured ? (
        <SecurityLockOverlay
          mode={lockOverlayMode}
          userLabel={profile?.email || user?.email || undefined}
          errorMessage={lockError}
          onSubmit={handleSecurityOverlaySubmit}
          onCancel={lockOverlayMode === 'fullscreen-exit' ? handleSecurityOverlayCancel : undefined}
        />
      ) : null}

    </div>
  );
};